import { useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from "react";

const join = (keys: string[]) => keys.join("\u0000");

/**
 * Reordering a list by a handle per entry. Pointer events make dragging work with mouse, pen and touch alike; from the keyboard Space
 * or Enter picks an entry up, the arrow keys move it, Space or Enter drops it and Escape puts it back. The new order is shown while
 * moving and handed to `onCommit` once, on dropping, and only when it changed.
 */
export function useSortable<T>(props: { items: T[]; keyOf: (item: T) => string; labelOf: (item: T) => string; onCommit: (keys: string[]) => Promise<boolean> }) {
  const { items, keyOf, labelOf, onCommit } = props;
  const original = items.map(keyOf);
  const signature = join(original);
  const [preview, setPreview] = useState<string[] | null>(null);
  const [moving, setMoving] = useState<{ key: string; by: "pointer" | "keyboard" } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const elements = useRef(new Map<string, HTMLElement>());
  const handles = useRef(new Map<string, HTMLElement>());

  // A new list from the server replaces whatever order was shown in the meantime.
  useEffect(() => setPreview(null), [signature]);

  const order = preview ?? original;
  const current = useRef(order);
  current.current = order;
  const byKey = new Map(items.map((item) => [keyOf(item), item]));
  const label = (key: string) => {
    const item = byKey.get(key);
    return item ? labelOf(item) : key;
  };
  const place = (key: string, keys: string[]) => `Position ${keys.indexOf(key) + 1} von ${keys.length}`;

  const moveTo = (key: string, index: number) => {
    const rest = current.current.filter((k) => k !== key);
    const target = Math.max(0, Math.min(rest.length, index));
    const next = [...rest.slice(0, target), key, ...rest.slice(target)];
    if (join(next) !== join(current.current)) {
      current.current = next;
      setPreview(next);
    }
    return next;
  };

  const finish = async (key: string, keys: string[]) => {
    setMoving(null);
    if (join(keys) === signature) {
      setPreview(null);
      setAnnouncement(`${label(key)} abgelegt, Reihenfolge unverändert.`);
      return;
    }
    setAnnouncement(`${label(key)} abgelegt, ${place(key, keys)}.`);
    if (!(await onCommit(keys))) setPreview(null);
  };

  const cancel = (key: string) => {
    setMoving(null);
    setPreview(null);
    setAnnouncement(`${label(key)} zurückgelegt.`);
  };

  /** Where the pointer is: the number of other entries whose middle lies above it. */
  const indexAt = (key: string, y: number) =>
    current.current.filter((k) => k !== key).filter((k) => {
      const box = elements.current.get(k)?.getBoundingClientRect();
      return box ? box.top + box.height / 2 < y : false;
    }).length;

  // Moving entries in the document can end the pointer capture of the handle, so the drag listens on the window.
  const latest = useRef({ moveTo, finish, cancel, indexAt, moving });
  latest.current = { moveTo, finish, cancel, indexAt, moving };
  useEffect(() => {
    if (moving?.by !== "pointer") return;
    const key = moving.key;
    const move = (e: globalThis.PointerEvent) => {
      if (e.clientY < 48) window.scrollBy(0, -12);
      else if (e.clientY > window.innerHeight - 48) window.scrollBy(0, 12);
      latest.current.moveTo(key, latest.current.indexAt(key, e.clientY));
    };
    const up = (e: globalThis.PointerEvent) => void latest.current.finish(key, latest.current.moveTo(key, latest.current.indexAt(key, e.clientY)));
    const cancelled = () => latest.current.cancel(key);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancelled);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancelled);
    };
  }, [moving]);

  // Reordering moves the elements in the document; the handle worked from the keyboard keeps the focus.
  useLayoutEffect(() => {
    if (moving?.by === "keyboard") handles.current.get(moving.key)?.focus();
  }, [join(order), moving]);

  return {
    /** The entries in the order to show, including a move not yet dropped. */
    items: order.map((key) => byKey.get(key)).filter((item): item is T => item !== undefined),
    /** Key of the entry being moved, e.g. to mark it. */
    moving: moving?.key ?? null,
    /** Text for a polite live region. */
    announcement,
    /** Ref for the element of an entry; its position decides where a dragged entry lands. */
    itemRef: (key: string) => (element: HTMLElement | null) => {
      if (element) elements.current.set(key, element);
      else elements.current.delete(key);
    },
    /** Props for the handle of an entry, a button. */
    handleProps: (key: string) => ({
      ref: (element: HTMLButtonElement | null) => {
        if (element) handles.current.set(key, element);
        else handles.current.delete(key);
      },
      type: "button" as const,
      "aria-label": `${label(key)} verschieben`,
      "aria-pressed": moving?.key === key,
      title: "Ziehen oder mit Leertaste und Pfeiltasten verschieben",
      onPointerDown: (e: PointerEvent<HTMLElement>) => {
        if (e.button !== 0 || moving) return;
        e.preventDefault();
        setMoving({ key, by: "pointer" });
      },
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        const grabbed = moving?.key === key && moving.by === "keyboard";
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          if (grabbed) void finish(key, current.current);
          else if (!moving) {
            setMoving({ key, by: "keyboard" });
            setAnnouncement(`${label(key)} aufgenommen, ${place(key, current.current)}. Pfeiltasten verschieben, Leertaste legt ab, Escape bricht ab.`);
          }
        } else if (grabbed && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
          e.preventDefault();
          const next = moveTo(key, current.current.indexOf(key) + (e.key === "ArrowUp" ? -1 : 1));
          setAnnouncement(`${label(key)}, ${place(key, next)}.`);
        } else if (grabbed && e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          cancel(key);
        }
      },
      onBlur: (e: FocusEvent<HTMLElement>) => {
        if (moving?.key !== key || moving.by !== "keyboard") return;
        const handle = e.currentTarget;
        // Moving the entry may blur its handle for a moment; only leaving it for good puts the entry back.
        window.setTimeout(() => {
          const still = latest.current.moving;
          if (still?.key === key && still.by === "keyboard" && document.activeElement !== handle) latest.current.cancel(key);
        });
      },
    }),
  };
}
